import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Avatar,
  Divider,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Spinner,
  InputGroup,
  InputRightElement,
  IconButton,
  FormErrorMessage,
  Progress,
  List,
  ListItem,
  ListIcon,
  Collapse,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Icon,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { FiEdit, FiCreditCard, FiCheck, FiX, FiEye, FiEyeOff, FiLock, FiTrash, FiPhone, FiRefreshCw } from 'react-icons/fi';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import api from '../services/api';
import SellerProfileCard from '../components/profile/SellerProfileCard';

const ProfilePage = () => {
  const { user, token, fetchUserProfile } = useAuth();
  const toast = useToast();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const cancelRef = useRef();

  // Seller profile
  const [sellerProfile, setSellerProfile] = useState(null);
  const [sellerProfileLoading, setSellerProfileLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [linkedinUrlInput, setLinkedinUrlInput] = useState('');
  const [companyWebsiteInput, setCompanyWebsiteInput] = useState('');

  // Phone verification step machine
  // 'idle' → 'entering' → 'verifying' → back to 'idle'
  const [phoneStep, setPhoneStep]       = useState('idle');
  const [pendingPhone, setPendingPhone] = useState('');
  const [otpValue, setOtpValue]         = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (!passwordData.new_password) {
      setPasswordStrength(0);
      return;
    }
    
    let strength = 0;
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(passwordData.new_password);
    
    if (passwordData.new_password.length >= 8) strength += 20;
    if (/[A-Z]/.test(passwordData.new_password)) strength += 20;
    if (/[a-z]/.test(passwordData.new_password)) strength += 20;
    if (/[0-9]/.test(passwordData.new_password)) strength += 20;
    if (hasSpecialChar) strength += 20;
    
    setPasswordStrength(strength);
  }, [passwordData.new_password]);

  useEffect(() => {
    if (user && token) {
      fetchSubscriptionStatus();
      fetchSellerProfile();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  const fetchSellerProfile = async () => {
    setSellerProfileLoading(true);
    try {
      const { data } = await api.get('/api/seller-profile/');
      setSellerProfile(data);
      setLinkedinUrlInput(data.linkedin_url || '');
      setCompanyWebsiteInput(data.company_website || '');
    } catch (err) {
      console.error('Failed to fetch seller profile', err);
    } finally {
      setSellerProfileLoading(false);
    }
  };

  const handleRegenerateAll = async () => {
    if (!linkedinUrlInput && !companyWebsiteInput) {
      toast({
        title: 'No URLs provided',
        description: 'Enter a LinkedIn URL and/or company website to regenerate.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setRegenerating(true);
    try {
      const { data } = await api.post('/api/seller-profile/generate', {
        linkedin_url: linkedinUrlInput || null,
        company_website: companyWebsiteInput || null,
      });
      setSellerProfile(data);
      toast({ title: 'Profile regenerated', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({
        title: 'Regeneration failed',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRegenerating(false);
    }
  };

  const handleRegenerateCompany = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post('/api/seller-profile/regenerate-field', {
        fields: ['company_name', 'company_summary'],
      });
      setSellerProfile(data);
      toast({ title: 'Company info regenerated', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({
        title: 'Regeneration failed',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRegenerating(false);
    }
  };

  const handleRegenerateLinkedIn = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post('/api/seller-profile/regenerate-field', {
        fields: ['alma_mater', 'interests', 'clubs_associations', 'area_of_expertise', 'location'],
      });
      setSellerProfile(data);
      toast({ title: 'LinkedIn fields regenerated', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({
        title: 'Regeneration failed',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRegenerating(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      if (!token) {
        setSubscriptionStatus({ subscription: 'free' });
        return;
      }
      localStorage.setItem('token', token);
      const response = await api.get('/api/payments/subscription-status');
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error in fetchSubscriptionStatus:', error);
      setSubscriptionStatus({ subscription: 'free' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscription = async () => {
    setSubscriptionLoading(true);
    try {
      if (!token) {
        toast({ title: 'Error', description: 'Please log in to manage your subscription', status: 'error', duration: 3000, isClosable: true });
        return;
      }
      localStorage.setItem('token', token);
      if (subscriptionStatus?.subscription && subscriptionStatus.subscription !== 'free') {
        const response = await api.post('/api/payments/create-portal-session', { return_url: window.location.href });
        window.location.href = response.data.url;
      } else {
        const response = await api.post('/api/payments/create-credit-purchase-session', {
          payment_type: 'subscription',
          product_key: 'basic_subscription',
          success_url: `${window.location.origin}/profile?success=true`,
          cancel_url: `${window.location.origin}/profile?canceled=true`
        });
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Subscription action failed:', error);
      toast({ title: 'Error', description: 'Failed to process subscription request. Please try again.', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      toast({ title: 'Success!', description: 'Your subscription has been activated. Welcome to Pro!', status: 'success', duration: 5000, isClosable: true });
      window.history.replaceState({}, document.title, window.location.pathname);
      if (user && token) setTimeout(() => fetchSubscriptionStatus(), 1000);
    } else if (urlParams.get('canceled') === 'true') {
      toast({ title: 'Subscription Canceled', description: 'You can try subscribing again anytime.', status: 'info', duration: 3000, isClosable: true });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast, user, token]);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) setPasswordErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const getStrengthColor = () => {
    if (passwordStrength < 20) return "red.500";
    if (passwordStrength < 40) return "orange.500";
    if (passwordStrength < 80) return "yellow.500";
    return "green.500";
  };
  
  const getStrengthLabel = () => {
    if (passwordStrength < 20) return "Weak";
    if (passwordStrength < 40) return "Fair";
    if (passwordStrength < 100) return "Good";
    return "Strong";
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    if (!passwordData.current_password.trim()) newErrors.current_password = 'Current password is required';
    if (!passwordData.new_password) {
      newErrors.new_password = 'New password is required';
    } else if (passwordData.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters long';
    } else if (!/[A-Z]/.test(passwordData.new_password)) {
      newErrors.new_password = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(passwordData.new_password)) {
      newErrors.new_password = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(passwordData.new_password)) {
      newErrors.new_password = 'Password must contain at least one number';
    }
    if (!passwordData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your new password';
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditProfile = () => setIsEditing(true);

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (user) setFormData({ first_name: user.first_name || '', last_name: user.last_name || '', email: user.email || '' });
  };

  const handleChangePassword = () => setIsChangingPassword(true);

  const handleCancelPasswordChange = () => {
    setIsChangingPassword(false);
    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    setPasswordErrors({});
    setPasswordStrength(0);
  };

  const formatPhoneDisplay = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const resetPhoneState = () => {
    setPhoneStep('idle');
    setPendingPhone('');
    setOtpValue('');
  };

  const handleSendOtp = async () => {
    if (!pendingPhone.trim()) return;
    setPhoneLoading(true);
    try {
      await api.post('/api/auth/send-phone-otp', { phone_number: pendingPhone.trim() });
      setPhoneStep('verifying');
      setOtpValue('');
      toast({ title: 'Code sent', description: `A 6-digit code was sent to ${pendingPhone.trim()}`, status: 'success', duration: 4000, isClosable: true });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send verification code. Please try again.';
      toast({ title: 'Error', description: msg, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpValue.trim().length !== 6) {
      toast({ title: 'Invalid code', description: 'Please enter the 6-digit code you received.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setPhoneLoading(true);
    try {
      await api.post('/api/auth/verify-phone-otp', { phone_number: pendingPhone.trim(), otp: otpValue.trim() });
      await fetchUserProfile();
      resetPhoneState();
      toast({ title: 'Phone number saved', description: 'Your phone number has been verified and saved.', status: 'success', duration: 4000, isClosable: true });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid code. Please try again.';
      toast({ title: 'Verification failed', description: msg, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setUpdateLoading(true);
    try {
      if (!token) { toast({ title: 'Error', description: 'Please log in to update your profile', status: 'error', duration: 3000, isClosable: true }); return; }
      localStorage.setItem('token', token);
      await api.post('/api/auth/update-profile', formData);
      await fetchUserProfile();
      toast({ title: 'Success', description: 'Profile updated successfully!', status: 'success', duration: 3000, isClosable: true });
      setIsEditing(false);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to update profile. Please try again.';
      toast({ title: 'Error', description: errorMessage, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleSavePassword = async () => {
    if (!validatePasswordForm()) return;
    setUpdateLoading(true);
    try {
      if (!token) { toast({ title: 'Error', description: 'Please log in to change your password', status: 'error', duration: 3000, isClosable: true }); return; }
      localStorage.setItem('token', token);
      await api.post('/api/auth/update-profile', { password: passwordData.new_password, current_password: passwordData.current_password });
      toast({ title: 'Success', description: 'Password changed successfully!', status: 'success', duration: 3000, isClosable: true });
      handleCancelPasswordChange();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to change password. Please try again.';
      toast({ title: 'Error', description: errorMessage, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteAccount = () => setShowDeleteConfirm(true);
  const handleCancelDelete = () => setShowDeleteConfirm(false);

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (!token) { toast({ title: 'Error', description: 'Please log in to delete your account', status: 'error', duration: 3000, isClosable: true }); return; }
      localStorage.setItem('token', token);
      await api.delete('/api/auth/users/me');
      localStorage.removeItem('token');
      window.location.href = '/login?account_deleted=true';
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to delete account. Please try again.';
      toast({ title: 'Error', description: errorMessage, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getTierLabel = () => {
    const tier = subscriptionStatus?.subscription || 'free';
    return { free: 'Free', starter: 'Starter', basic: 'Basic', pro: 'Pro' }[tier] || 'Free';
  };

  if (loading) {
    return (
      <Flex align="center" justify="center" minH="100vh" bg="#0a0a0a">
        <Spinner size="xl" color="gray.600" />
      </Flex>
    );
  }

  if (!user) {
    return (
      <Flex align="center" justify="center" minH="100vh" bg="#0a0a0a">
        <Text color="#a3a3a3">Please log in to view your profile.</Text>
      </Flex>
    );
  }

  // Shared input styling
  const inputProps = {
    bg: "#171717",
    borderColor: "rgba(255,255,255,0.12)",
    color: "#fafafa",
    fontSize: "sm",
    _placeholder: { color: 'rgba(255,255,255,0.25)' },
    _hover: { borderColor: 'rgba(255,255,255,0.2)' },
    _focus: { borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' },
    _readOnly: { opacity: 0.6, cursor: 'default' },
  };

  const primaryBtn = {
    bg: "#fafafa",
    color: "#0a0a0a",
    borderRadius: "full",
    fontWeight: "600",
    fontSize: "13px",
    h: "36px",
    _hover: { opacity: 0.85 },
  };

  const ghostBtn = {
    bg: "transparent",
    borderRadius: "full",
    fontWeight: "500",
    fontSize: "13px",
    h: "36px",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "rgba(255,255,255,0.6)",
    _hover: { borderColor: "rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.9)", bg: "rgba(255,255,255,0.04)" },
  };

  const dangerBtn = {
    bg: "transparent",
    borderRadius: "full",
    fontWeight: "500",
    fontSize: "13px",
    h: "36px",
    border: "1px solid rgba(239,68,68,0.3)",
    color: "rgba(239,68,68,0.7)",
    _hover: { borderColor: "rgba(239,68,68,0.5)", color: "rgba(239,68,68,0.9)", bg: "rgba(239,68,68,0.06)" },
  };

  return (
    <Flex align="flex-start" justify="center" minH="100vh" bg="#0a0a0a" pt={10} pb={16} px={6}>
      <Box maxW="xl" w="100%">
        <VStack spacing={6} align="stretch">

          {/* Seller Profile section */}
          <Box>
            <HStack justify="space-between" mb={4}>
              <VStack align="flex-start" spacing={0}>
                <Heading size="sm" color="#fafafa" fontWeight="600" letterSpacing="-0.01em">
                  Seller Profile
                </Heading>
                <Text fontSize="xs" color="#737373" mt={0.5}>
                  Used by AI agents when generating personalized outreach
                </Text>
              </VStack>
            </HStack>

            {/* URL inputs + regenerate buttons */}
            <Box
              bg="#0f0f0f"
              border="1px solid rgba(255,255,255,0.10)"
              borderRadius="12px"
              p={4}
              mb={4}
            >
              <VStack spacing={3} align="stretch">
                <HStack spacing={3} flexWrap="wrap">
                  <Box flex="1" minW="200px">
                    <Text fontSize="xs" color="#a3a3a3" mb={1.5} textTransform="uppercase" letterSpacing="0.06em">
                      LinkedIn URL
                    </Text>
                    <Input
                      value={linkedinUrlInput}
                      onChange={(e) => setLinkedinUrlInput(e.target.value)}
                      placeholder="https://linkedin.com/in/yourprofile"
                      size="sm"
                      bg="#171717"
                      borderColor="rgba(255,255,255,0.12)"
                      color="#fafafa"
                      fontSize="sm"
                      _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                      _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                      _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    />
                  </Box>
                  <Box flex="1" minW="200px">
                    <Text fontSize="xs" color="#a3a3a3" mb={1.5} textTransform="uppercase" letterSpacing="0.06em">
                      Company Website
                    </Text>
                    <Input
                      value={companyWebsiteInput}
                      onChange={(e) => setCompanyWebsiteInput(e.target.value)}
                      placeholder="https://yourcompany.com"
                      size="sm"
                      bg="#171717"
                      borderColor="rgba(255,255,255,0.12)"
                      color="#fafafa"
                      fontSize="sm"
                      _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                      _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                      _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    />
                  </Box>
                </HStack>
                <HStack spacing={2} flexWrap="wrap">
                  <Button
                    size="xs"
                    variant="outline"
                    color="#a3a3a3"
                    borderColor="rgba(255,255,255,0.15)"
                    leftIcon={<FiRefreshCw />}
                    isLoading={regenerating}
                    loadingText="Regenerating..."
                    onClick={handleRegenerateAll}
                    _hover={{ color: '#fafafa', borderColor: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.04)' }}
                    _focus={{ boxShadow: 'none' }}
                  >
                    Regenerate All
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    color="#a3a3a3"
                    borderColor="rgba(255,255,255,0.15)"
                    isLoading={regenerating}
                    onClick={handleRegenerateCompany}
                    _hover={{ color: '#fafafa', borderColor: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.04)' }}
                    _focus={{ boxShadow: 'none' }}
                  >
                    Regenerate Company Info
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    color="#a3a3a3"
                    borderColor="rgba(255,255,255,0.15)"
                    isLoading={regenerating}
                    onClick={handleRegenerateLinkedIn}
                    _hover={{ color: '#fafafa', borderColor: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.04)' }}
                    _focus={{ boxShadow: 'none' }}
                  >
                    Regenerate LinkedIn Fields
                  </Button>
                </HStack>
              </VStack>
            </Box>

            {sellerProfileLoading ? (
              <Box py={8} textAlign="center">
                <Spinner color="#a3a3a3" size="sm" />
              </Box>
            ) : (
              <SellerProfileCard
                profile={sellerProfile}
                onUpdate={(updated) => setSellerProfile(updated)}
                firstName={user?.first_name || ''}
                lastName={user?.last_name || ''}
              />
            )}
          </Box>

          {/* Profile header */}
          <Box
            p={6}
            borderRadius="12px"
            border="1px solid rgba(255,255,255,0.10)"
            bg="#0f0f0f"
          >
            <HStack spacing={4}>
              <Avatar
                size="lg"
                name={`${user.first_name} ${user.last_name}`}
                bg="rgba(255,255,255,0.08)"
                color="#fafafa"
              />
              <VStack align="start" spacing={1}>
                <HStack spacing={2} align="center">
                  <Heading size="md" color="#fafafa" fontWeight="700" letterSpacing="-0.01em">
                    {`${user.first_name} ${user.last_name}`}
                  </Heading>
                  {subscriptionStatus && (
                    <Box
                      px={1.5}
                      py="2px"
                      borderRadius="full"
                      bg="rgba(255,255,255,0.06)"
                      border="1px solid rgba(255,255,255,0.12)"
                    >
                      <Text fontSize="10px" fontWeight="500" color="rgba(255,255,255,0.45)" letterSpacing="0.06em" textTransform="uppercase">
                        {getTierLabel()}
                      </Text>
                    </Box>
                  )}
                </HStack>
                <Text fontSize="sm" color="#a3a3a3">{user.email}</Text>
              </VStack>
            </HStack>
          </Box>

          {/* Account information */}
          <Box
            p={6}
            borderRadius="12px"
            border="1px solid rgba(255,255,255,0.10)"
            bg="#0f0f0f"
          >
            <VStack spacing={5} align="stretch">
              <HStack justify="space-between" align="center">
                <Text fontSize="sm" fontWeight="600" color="#fafafa" letterSpacing="-0.01em">
                  Account Information
                </Text>
                {!isEditing && (
                  <Button
                    {...ghostBtn}
                    leftIcon={<Icon as={FiEdit} boxSize={3.5} />}
                    isDisabled={isChangingPassword}
                    onClick={handleEditProfile}
                  >
                    Edit
                  </Button>
                )}
              </HStack>

              <Divider borderColor="rgba(255,255,255,0.08)" />

              <FormControl id="first_name">
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>First Name</FormLabel>
                <Input
                  value={isEditing ? formData.first_name : user.first_name}
                  isReadOnly={!isEditing}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  {...inputProps}
                />
              </FormControl>

              <FormControl id="last_name">
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Last Name</FormLabel>
                <Input
                  value={isEditing ? formData.last_name : user.last_name}
                  isReadOnly={!isEditing}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  {...inputProps}
                />
              </FormControl>

              <FormControl id="email">
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Email</FormLabel>
                <Input
                  value={isEditing ? formData.email : user.email}
                  isReadOnly={!isEditing}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  {...inputProps}
                />
              </FormControl>

              {/* Phone number — self-contained verification step machine */}
              <FormControl id="phone_number">
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>
                  <HStack spacing={1.5} align="center">
                    <Icon as={FiPhone} boxSize={3} />
                    <span>Phone Number</span>
                    {user.phone_number && (
                      <Box
                        px={1.5} py="1px" borderRadius="full"
                        bg="rgba(74,222,128,0.08)" border="1px solid rgba(74,222,128,0.2)"
                      >
                        <Text fontSize="9px" fontWeight="600" color="rgba(74,222,128,0.7)" letterSpacing="0.06em" textTransform="uppercase">
                          Verified
                        </Text>
                      </Box>
                    )}
                  </HStack>
                </FormLabel>

                {phoneStep === 'idle' && (
                  <HStack spacing={2}>
                    <Input
                      value={user.phone_number || ''}
                      isReadOnly
                      placeholder="Not set"
                      {...inputProps}
                      _placeholder={{ color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}
                    />
                    <Button
                      {...ghostBtn}
                      flexShrink={0}
                      leftIcon={<Icon as={FiPhone} boxSize={3.5} />}
                      isDisabled={isEditing || isChangingPassword || updateLoading}
                      onClick={() => {
                        setPendingPhone(formatPhoneDisplay(user.phone_number || ''));
                        setPhoneStep('entering');
                      }}
                    >
                      {user.phone_number ? 'Change' : 'Add'}
                    </Button>
                  </HStack>
                )}

                {phoneStep === 'entering' && (
                  <VStack spacing={3} align="stretch">
                    <HStack spacing={2}>
                      <Input
                        value={pendingPhone}
                        onChange={(e) => setPendingPhone(formatPhoneDisplay(e.target.value))}
                        placeholder="(555) 000-0000"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                        {...inputProps}
                      />
                      <Button
                        {...primaryBtn}
                        flexShrink={0}
                        onClick={handleSendOtp}
                        isLoading={phoneLoading}
                        loadingText="Sending..."
                        isDisabled={!pendingPhone.trim()}
                      >
                        Send Code
                      </Button>
                      <Button
                        {...ghostBtn}
                        flexShrink={0}
                        leftIcon={<Icon as={FiX} boxSize={3.5} />}
                        onClick={resetPhoneState}
                        isDisabled={phoneLoading}
                      >
                        Cancel
                      </Button>
                    </HStack>
                    <Text fontSize="xs" color="rgba(255,255,255,0.25)">
                      We'll send a 6-digit verification code via SMS.
                    </Text>
                  </VStack>
                )}

                {phoneStep === 'verifying' && (
                  <VStack spacing={3} align="stretch">
                    <Text fontSize="xs" color="rgba(255,255,255,0.45)">
                      Code sent to <strong style={{ color: 'rgba(255,255,255,0.7)' }}>{pendingPhone}</strong>. Enter it below — expires in 5 minutes.
                    </Text>
                    <HStack spacing={2}>
                      <Input
                        value={otpValue}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtpValue(val);
                        }}
                        placeholder="000000"
                        maxLength={6}
                        autoFocus
                        letterSpacing="0.35em"
                        fontWeight="600"
                        fontSize="md"
                        textAlign="center"
                        onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
                        {...inputProps}
                      />
                      <Button
                        {...primaryBtn}
                        flexShrink={0}
                        leftIcon={<Icon as={FiCheck} boxSize={3.5} />}
                        onClick={handleVerifyOtp}
                        isLoading={phoneLoading}
                        loadingText="Verifying..."
                        isDisabled={otpValue.length !== 6}
                      >
                        Verify
                      </Button>
                    </HStack>
                    <HStack spacing={3}>
                      <Button
                        variant="link"
                        fontSize="xs"
                        color="rgba(255,255,255,0.35)"
                        leftIcon={<Icon as={FiRefreshCw} boxSize={3} />}
                        _hover={{ color: 'rgba(255,255,255,0.6)' }}
                        onClick={() => { setOtpValue(''); handleSendOtp(); }}
                        isDisabled={phoneLoading}
                      >
                        Resend code
                      </Button>
                      <Text fontSize="xs" color="rgba(255,255,255,0.15)">·</Text>
                      <Button
                        variant="link"
                        fontSize="xs"
                        color="rgba(255,255,255,0.35)"
                        _hover={{ color: 'rgba(255,255,255,0.6)' }}
                        onClick={resetPhoneState}
                        isDisabled={phoneLoading}
                      >
                        Cancel
                      </Button>
                    </HStack>
                  </VStack>
                )}
              </FormControl>

              {isEditing && (
                <HStack justify="flex-end" spacing={2} pt={1}>
                  <Button {...ghostBtn} leftIcon={<Icon as={FiX} boxSize={3.5} />} onClick={handleCancelEdit} isDisabled={updateLoading}>
                    Cancel
                  </Button>
                  <Button {...primaryBtn} leftIcon={<Icon as={FiCheck} boxSize={3.5} />} onClick={handleSaveProfile} isLoading={updateLoading} loadingText="Saving...">
                    Save Changes
                  </Button>
                </HStack>
              )}
            </VStack>
          </Box>

          {/* Password */}
          <Box
            p={6}
            borderRadius="12px"
            border="1px solid rgba(255,255,255,0.10)"
            bg="#0f0f0f"
          >
            <VStack spacing={5} align="stretch">
              <HStack justify="space-between" align="center">
                <Text fontSize="sm" fontWeight="600" color="#fafafa" letterSpacing="-0.01em">
                  Password
                </Text>
                {!isChangingPassword && (
                  <Button
                    {...ghostBtn}
                    leftIcon={<Icon as={FiLock} boxSize={3.5} />}
                    isDisabled={isEditing || updateLoading}
                    onClick={handleChangePassword}
                  >
                    Change Password
                  </Button>
                )}
              </HStack>

              <Collapse in={isChangingPassword} animateOpacity>
                <VStack spacing={4} align="stretch">
                  <Divider borderColor="rgba(255,255,255,0.08)" />

                  <FormControl id="current_password" isRequired isInvalid={passwordErrors.current_password}>
                    <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Current Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordData.current_password}
                        onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                        placeholder="Enter your current password"
                        {...inputProps}
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                          icon={showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                          size="sm" variant="ghost" color="#a3a3a3"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          _hover={{ color: '#fafafa', bg: 'transparent' }}
                          _focus={{ boxShadow: 'none' }}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage fontSize="xs">{passwordErrors.current_password}</FormErrorMessage>
                  </FormControl>

                  <FormControl id="new_password" isRequired isInvalid={passwordErrors.new_password}>
                    <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>New Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.new_password}
                        onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                        placeholder="Enter your new password"
                        {...inputProps}
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                          icon={showNewPassword ? <FiEyeOff /> : <FiEye />}
                          size="sm" variant="ghost" color="#a3a3a3"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          _hover={{ color: '#fafafa', bg: 'transparent' }}
                          _focus={{ boxShadow: 'none' }}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {passwordData.new_password && (
                      <Box mt={2.5}>
                        <Flex align="center" justify="space-between" mb={1.5}>
                          <Text fontSize="xs" color={getStrengthColor()}>{getStrengthLabel()}</Text>
                          <Text fontSize="xs" color="rgba(255,255,255,0.3)">{passwordStrength}%</Text>
                        </Flex>
                        <Progress value={passwordStrength} colorScheme={getStrengthColor().split('.')[0]} size="xs" borderRadius="full" bg="rgba(255,255,255,0.08)" />
                        <Box mt={3}>
                          <Text fontSize="xs" color="rgba(255,255,255,0.3)" mb={1.5}>Password must have:</Text>
                          <List spacing={1}>
                            {[
                              { check: passwordData.new_password.length >= 8, label: 'At least 8 characters' },
                              { check: /[A-Z]/.test(passwordData.new_password), label: 'At least one uppercase letter' },
                              { check: /[a-z]/.test(passwordData.new_password), label: 'At least one lowercase letter' },
                              { check: /[0-9]/.test(passwordData.new_password), label: 'At least one number' },
                            ].map(({ check, label }) => (
                              <ListItem key={label} display="flex" alignItems="center">
                                <ListIcon as={check ? CheckIcon : CloseIcon} color={check ? "#4ade80" : "rgba(255,255,255,0.25)"} boxSize={2.5} />
                                <Text fontSize="xs" color={check ? "#4ade80" : "rgba(255,255,255,0.4)"}>{label}</Text>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Box>
                    )}
                    <FormErrorMessage fontSize="xs">{passwordErrors.new_password}</FormErrorMessage>
                  </FormControl>

                  <FormControl id="confirm_password" isRequired isInvalid={passwordErrors.confirm_password}>
                    <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Confirm New Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordData.confirm_password}
                        onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                        placeholder="Confirm your new password"
                        {...inputProps}
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                          icon={showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                          size="sm" variant="ghost" color="#a3a3a3"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          _hover={{ color: '#fafafa', bg: 'transparent' }}
                          _focus={{ boxShadow: 'none' }}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage fontSize="xs">{passwordErrors.confirm_password}</FormErrorMessage>
                  </FormControl>

                  <HStack justify="flex-end" spacing={2} pt={1}>
                    <Button {...ghostBtn} leftIcon={<Icon as={FiX} boxSize={3.5} />} onClick={handleCancelPasswordChange} isDisabled={updateLoading}>
                      Cancel
                    </Button>
                    <Button {...primaryBtn} leftIcon={<Icon as={FiCheck} boxSize={3.5} />} onClick={handleSavePassword} isLoading={updateLoading} loadingText="Saving...">
                      Change Password
                    </Button>
                  </HStack>
                </VStack>
              </Collapse>

              {!isChangingPassword && (
                <Text fontSize="xs" color="rgba(255,255,255,0.25)">
                  Use a strong password with uppercase, lowercase, numbers, and special characters.
                </Text>
              )}
            </VStack>
          </Box>

          {/* Danger zone */}
          <Box
            p={6}
            borderRadius="12px"
            border="1px solid rgba(239,68,68,0.15)"
            bg="#0f0f0f"
          >
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" fontWeight="600" color="rgba(239,68,68,0.8)" letterSpacing="-0.01em">
                Danger Zone
              </Text>
              <Divider borderColor="rgba(239,68,68,0.1)" />
              <HStack justify="space-between" align="center">
                <VStack align="start" spacing={0.5}>
                  <Text fontSize="sm" color="#fafafa" fontWeight="500">Delete account</Text>
                  <Text fontSize="xs" color="#a3a3a3">Permanently delete your account and all associated data.</Text>
                </VStack>
                <Button
                  {...dangerBtn}
                  leftIcon={<Icon as={FiTrash} boxSize={3.5} />}
                  isDisabled={isEditing || isChangingPassword || updateLoading || isDeleting}
                  isLoading={isDeleting}
                  loadingText="Deleting..."
                  onClick={handleDeleteAccount}
                  flexShrink={0}
                >
                  Delete
                </Button>
              </HStack>
            </VStack>
          </Box>

        </VStack>
      </Box>

      {/* Delete confirmation dialog */}
      <AlertDialog
        isOpen={showDeleteConfirm}
        leastDestructiveRef={cancelRef}
        onClose={handleCancelDelete}
        isCentered
      >
        <AlertDialogOverlay bg="blackAlpha.700" />
        <AlertDialogContent bg="#0a0a0a" color="#fafafa" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
          <AlertDialogHeader fontSize="md" fontWeight="700" letterSpacing="-0.01em" pb={2}>
            Delete Account
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text fontSize="sm" color="#a3a3a3" lineHeight="1.7">
              Are you sure? All your information will be permanently deleted. This action cannot be undone.
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter gap={2} pt={2}>
            <Button
              ref={cancelRef}
              {...ghostBtn}
              onClick={handleCancelDelete}
              isDisabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              {...dangerBtn}
              onClick={handleConfirmDelete}
              isLoading={isDeleting}
              loadingText="Deleting..."
            >
              Delete Account
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </Flex>
  );
};

export default ProfilePage;
